import { Button } from '@droidmap/frontend-ui'
import type { MyOrganisationInvitation } from '@droidmap/organisation-service-contract'

import { InvitationCard } from './InvitationCard'

export interface PendingInvitationsPanelProps {
  invitations: MyOrganisationInvitation[]
  /** The list failed to load. */
  isError?: boolean
  onRetry?: () => void
  onAccept: (invitation: MyOrganisationInvitation) => void
  onDecline: (invitation: MyOrganisationInvitation) => void
  /** The invitation with an action in flight, and which action. */
  pending?: { invitationId: string; action: 'accept' | 'decline' }
  /** A failed accept / decline, already worded for the user. */
  actionError?: string
}

/**
 * Open invitations on the organisations dashboard (#3058), fed by
 * `GET /invitations/me`. Renders nothing when there are none, so it can sit
 * above the org grid unconditionally.
 *
 * A failed load is shown as a small inline notice rather than replacing the
 * page: the org grid below still works, and invitations are a secondary path.
 */
export function PendingInvitationsPanel({
  invitations,
  isError = false,
  onRetry,
  onAccept,
  onDecline,
  pending,
  actionError,
}: PendingInvitationsPanelProps) {
  if (isError) {
    return (
      <section
        aria-label="Pending invitations"
        className="border-border bg-panel mb-7 flex flex-wrap items-center justify-between gap-3 rounded-lg border p-4"
      >
        <p className="text-text-dim text-sm">
          Couldn’t check for invitations to join an organisation.
        </p>
        {onRetry && (
          <Button variant="outline" size="sm" onClick={onRetry}>
            Retry
          </Button>
        )}
      </section>
    )
  }

  if (invitations.length === 0) return null

  return (
    <section aria-label="Pending invitations" className="mb-7">
      <div className="border-border mb-3.5 flex items-baseline justify-between border-b pb-2.5">
        <h2 className="text-micro text-text-dim">Pending invitations</h2>
        <span className="text-text-mute font-mono text-[11px] uppercase tracking-wider">
          {invitations.length} open
        </span>
      </div>
      <div className="flex flex-col gap-3">
        {invitations.map((invitation) => (
          <InvitationCard
            key={invitation.id}
            invitation={invitation}
            onAccept={() => onAccept(invitation)}
            onDecline={() => onDecline(invitation)}
            pendingAction={
              pending?.invitationId === invitation.id
                ? pending.action
                : undefined
            }
            disabled={
              pending !== undefined && pending.invitationId !== invitation.id
            }
          />
        ))}
      </div>
      {actionError && (
        <p role="alert" className="text-danger mt-3 text-sm">
          {actionError}
        </p>
      )}
    </section>
  )
}
